import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
import { isResponseAPI, ResponseAPI, ResponseJSON } from 'api';
import { getListUsersByFilters } from 'api/user/user.api';
import ContainerPainel from 'components/container_painel';
import InputDados from 'components/input_dados';
import { EmptyContent, ErrorDialog } from 'components/shared';
import { ContainerForm } from 'components/shared/container_form';
import LoginDataDisplay from 'components/user/login_data_display';
import PersonalDataDisplay from 'components/user/personal_data_display';
import RegistrationDataDisplay from 'components/user/registration_data_display';
import { useAuth } from 'contexts/auth/auth_context';
import { ModuleEnum, PermissionType, usePermissions } from 'contexts/auth/permission_context';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import { formatCPF } from 'services/utils/format_cpf';
import { UserDetailsData } from './type';
import { formatUserDetails as formatUserDetailsHelper, parseValidityDates, shouldShowFileSection, isMunicipalRepresentative } from './helpers';
import { downloadFileByUUID, getFileByUUID } from 'api/file/file.api';
import { getValidityFile } from 'api/validity/validity.api';

const UserView: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const userPermission = user?.permission?.name;
  const viewFullCPF =
    !userPermission?.includes('OPERADOR') &&
    !userPermission?.includes('CONSELHEIRO');

  useEffect(() => {
    if (uuid) {
      if (user?.uuid !== uuid && !hasPermission(ModuleEnum.USERS, PermissionType.UPDATE)) {
        alert("Você não tem permissão para acessar esta página.");
        navigate(ROUTES.HOME);
      }
    }
  }, [hasPermission, navigate, user?.uuid, uuid]);

  const [isEmptyContent, setIsEmptyContent] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [invalidRequest, setInvalidRequest] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetailsData | null>(null);
  const [permissaoSelelcionadaNome, setpermissaoSelelcionadaNome] =
    useState<string>();

  const [dataVigenciaInicial, setdataVigenciaInicial] = useState<string>('');
  const [dataVigenciaFinal, setdataVigenciaFinal] = useState<string>('');
  const [dataVigenciaIndeterminada, setdataVigenciaIndeterminada] =
    useState<boolean>(false);

  const [arquivoAnexo, setArquivoAnexo] = useState<any>(null);

  const handleVigencia = useCallback((u: any) => {
    if (u?.dadosVigencia) {
      setVigenciaData(u.dadosVigencia);
      return;
    }
    if (u?.validity) setVigenciaDataFromValidity(u.validity);
  }, []);



  const handleApiError = (resp: ResponseAPI) => {
    alert(resp.Mensagem);
    setIsEmptyContent(true);
  };

  const formatUserDetails = useCallback((user: any) => formatUserDetailsHelper(user, viewFullCPF), [viewFullCPF]);

  const processUserData = useCallback(
    (data: ResponseJSON) => {
      const user = data?.data?.[0];
      setUserDetails(formatUserDetails(user));  // Usando formatUserDetails
      setpermissaoSelelcionadaNome(user?.permission?.name);
      handleVigencia(user);
      return user;
    },
    [handleVigencia, formatUserDetails]
  );

  const loadUserData = useCallback(async (uuid: string) => {
    const resp = await getListUsersByFilters({ limit: '10', uuid });
    if (isResponseAPI(resp)) {
      handleApiError(resp);
      return null;
    }
    return resp;
  }, []);






  const loadUserFile = useCallback(async (user: any) => {
    const validityUuid: string | undefined = user?.validity?.uuid;
    if (validityUuid) {
      try {
        const file = await getValidityFile(validityUuid);
        setArquivoAnexo(file);
      } catch (e: any) {
        console.warn('[UserView] sem arquivo vinculado à vigência ou 404', e?.response?.status);
        setArquivoAnexo(null);
        await fetchFallbackFile(user);
      }
    } else {
      setArquivoAnexo(null);
    }
  }, []);

  const fetchFallbackFile = async (user: any) => {
    const fileUuid = user?.validity?.fileUuid ?? user?.validity?.file?.uuid;
    if (fileUuid) {
      try {
        const meta = await getFileByUUID(fileUuid);
        const fileMeta = meta?.data && !meta?.uuid ? meta.data : meta;
        setArquivoAnexo(fileMeta);
      } catch (err) {
        console.error('[UserView] fallback getFileByUUID falhou:', err);
      }
    }
  };

  const handleError = (error: any) => {
    console.error('[UserView] erro geral:', error);
    setIsEmptyContent(true);
    setInvalidRequest(true);
  };

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);

        if (!uuid) {
          setIsEmptyContent(true);
          return;
        }

        const data = await loadUserData(uuid);
        if (!data) return;

        const user = processUserData(data);
        await loadUserFile(user);

        setIsEmptyContent(false);
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [uuid, loadUserData, processUserData, loadUserFile]);


  const setVigenciaData = (dadosVigencia: any) => {
    setdataVigenciaInicial(dadosVigencia.dataInicial);
    setdataVigenciaFinal(dadosVigencia.dataFinal);
    setdataVigenciaIndeterminada(dadosVigencia.vigenciaIndeterminada);
  };

  const setVigenciaDataFromValidity = (validityData: any) => {
    const { initialDate, finalDate, indeterminate } = parseValidityDates(validityData || {});
    setdataVigenciaInicial(initialDate);
    setdataVigenciaFinal(finalDate);
    setdataVigenciaIndeterminada(indeterminate);
  };

  const handleRefresh = () => {
    window.location.reload();
    setInvalidRequest(false);
  };

  const handleBack = () => {
    navigate(ROUTES.MANAGE_USER);
  };

  const getTipoRepresentanteMunicipal = (): boolean => isMunicipalRepresentative(permissaoSelelcionadaNome);

  const renderLoadingState = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
      }}
    >
      <CircularProgress />
    </Box>
  );

  const renderEmptyState = () => <EmptyContent />;

  const renderVigenciaSection = () => {
    if (dataVigenciaInicial && dataVigenciaFinal) {
      return (
        <ContainerForm title="Dados da Vigência" width="1150px">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <InputDados
                placeholder="Data Inicial de Vigência"
                label="Data Inicial de Vigência"
                mask="99/99/9999"
                value={dataVigenciaInicial}
                disabled={true}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputDados
                placeholder="Data Final de Vigência"
                label="Data Final de Vigência"
                mask="99/99/9999"
                value={dataVigenciaFinal}
                disabled={true}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={dataVigenciaIndeterminada} disabled={true} />}
                label="Data final indeterminada"
              />
            </Grid>
          </Grid>
        </ContainerForm>
      );
    }
    return null;
  };

  const renderFileSection = () => {
    if (!shouldShowFileSection(user?.permission?.name)) return null;


    const fileData = arquivoAnexo;

    if (!fileData?.uuid) {
      return <Typography sx={{ marginTop: '20px' }} color="text.secondary">Nenhum anexo selecionado.</Typography>;
    }

    const handleVisualizar = async () => {
      const win = window.open('', '_blank');
      try {
        const blob = await downloadFileByUUID(fileData.uuid);
        const url = URL.createObjectURL(blob);
        if (win) {
          win.location.href = url;
        } else {
          window.open(url, '_blank');
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (err) {
        console.error('Erro ao visualizar arquivo:', err);
        if (win) win.close();
      }
    };

    const handleDownload = async () => {
      try {
        const blob = await downloadFileByUUID(fileData.uuid);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileData.name || 'arquivo';
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Erro ao baixar arquivo:', err);
      }
    };

    return (
      <Box sx={{ p: 1, width: '1150px' }}>
        <ContainerForm title="Arquivo anexado">
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Typography sx={{ fontWeight: 500 }}>
                {fileData.name || 'Documento'}
              </Typography>
              <Typography variant="body2">
                {(fileData.documentType || fileData.mimeType || 'Tipo desconhecido')}
                {fileData.size ? ` • ${(fileData.size / 1024).toFixed(1)} KB` : ''}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
            >
              <Button variant="outlined" onClick={handleVisualizar}>Visualizar</Button>
              <Button variant="contained" onClick={handleDownload}>Baixar</Button>
            </Grid>
          </Grid>
        </ContainerForm>
      </Box>
    );
  };






  const renderUserContent = () => (
    <Box sx={{ backgroundColor: '#FAFAFA' }}>
      <Box sx={{ paddingTop: '50px' }}>
        <Typography
          sx={{
            fontFamily: 'Roboto',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '22.88px',
            letterSpacing: '0.17px',
            textAlign: 'center',
            marginTop: '20px',
          }}
        >
          Visualize todos os dados do usuário no sistema.
        </Typography>
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'nowrap',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {userDetails?.personalData && (
          <PersonalDataDisplay values={userDetails.personalData} isView={true} />
        )}
        {userDetails?.loginData && (
          <LoginDataDisplay values={userDetails.loginData} isView={true} />
        )}
        {userDetails?.registrationData && (
          <RegistrationDataDisplay values={userDetails.registrationData} isView={true} />
        )}

        {getTipoRepresentanteMunicipal() && renderVigenciaSection()}
        {renderFileSection()}
      </Box>

      <Stack marginY="2rem">
        <Button
          variant="text"
          onClick={handleBack}
          sx={{
            fontFamily: 'Roboto',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: '0.4px',
            textAlign: 'left',
            color: '#0048B6',
            marginRight: '20px',
          }}
        >
          VOLTAR
        </Button>
      </Stack>
    </Box>
  );

  const renderMainContent = () => {
    if (isLoading) return renderLoadingState();
    if (isEmptyContent) return renderEmptyState();
    return renderUserContent();
  };

  return (
    <ContainerPainel pagina={'Visualizar Usuários'} showBackButton={true}>
      {renderMainContent()}

      <ErrorDialog
        open={invalidRequest}
        title="Ocorreu um erro na sua solicitação"
        message1="Deseja atualizar a página atual?"
        button1Text="FECHAR"
        button1Action={() => setInvalidRequest(false)}
        button1Color="inherit"
        button2Text="ATUALIZAR"
        button2Action={handleRefresh}
        button2Color="primary"
      />
    </ContainerPainel>
  );
};

export default UserView;
